import { EventEmitter } from 'events';
import path from 'path';
import fs from 'fs-extra';
import { logger } from '../utils/logger';
import { z } from 'zod';
import { v4 as uuidv4 } from 'uuid';
import { TeamManager } from './team-manager';
import { spawn } from 'child_process';
import * as http from 'http';

// Notification schema
export const notificationSchema = z.object({
  id: z.string(),
  type: z.enum(['info', 'success', 'warning', 'error', 'task', 'team']),
  title: z.string(),
  message: z.string(),
  from: z.string(),
  to: z.string().optional(), // undefined = broadcast
  timestamp: z.string().datetime(),
  read: z.boolean().default(false),
  priority: z.enum(['low', 'medium', 'high', 'urgent']).default('medium'),
  data: z.any().optional(),
  actions: z
    .array(
      z.object({
        label: z.string(),
        action: z.string(),
        primary: z.boolean().optional(),
      })
    )
    .optional(),
});

export type Notification = z.infer<typeof notificationSchema>;

export interface NotificationChannel {
  id: string;
  name: string;
  type: 'mcp' | 'sse' | 'webhook' | 'file';
  enabled: boolean;
  config?: any;
}

export interface NotificationOptions {
  projectRoot?: string;
  enableMCP?: boolean;
  enableSSE?: boolean;
  ssePort?: number;
  channels?: NotificationChannel[];
}

export class NotificationManager extends EventEmitter {
  private projectRoot: string;
  private notificationsDir: string;
  private teamManager: TeamManager;
  private options: NotificationOptions;
  private sseServer: http.Server | null = null;
  private sseClients: Set<http.ServerResponse> = new Set();
  private mcpProcess: any = null;
  private channels: Map<string, NotificationChannel> = new Map();

  constructor(teamManager: TeamManager, options: NotificationOptions = {}) {
    super();
    this.teamManager = teamManager;
    this.options = {
      projectRoot: options.projectRoot || process.cwd(),
      enableMCP: options.enableMCP ?? true,
      enableSSE: options.enableSSE ?? true,
      ssePort: options.ssePort || 3456,
      channels: options.channels || [],
    };
    this.projectRoot = this.options.projectRoot;
    this.notificationsDir = path.join(this.projectRoot, '.workflow/notifications');

    // Initialize default channels
    this.initializeChannels();
  }

  private initializeChannels(): void {
    // File channel (always enabled)
    this.channels.set('file', {
      id: 'file',
      name: 'File Storage',
      type: 'file',
      enabled: true,
    });

    // MCP channel
    if (this.options.enableMCP) {
      this.channels.set('mcp', {
        id: 'mcp',
        name: 'MCP Notifications',
        type: 'mcp',
        enabled: true,
        config: {
          tool: 'notify',
          server: '@gongfu/mcp-server',
        },
      });
    }

    // SSE channel
    if (this.options.enableSSE) {
      this.channels.set('sse', {
        id: 'sse',
        name: 'Server-Sent Events',
        type: 'sse',
        enabled: true,
        config: {
          port: this.options.ssePort,
        },
      });
    }

    // Custom channels
    this.options.channels?.forEach(channel => {
      this.channels.set(channel.id, channel);
    });
  }

  async initialize(): Promise<void> {
    await fs.ensureDir(this.notificationsDir);
    await fs.ensureDir(path.join(this.notificationsDir, 'inbox'));
    await fs.ensureDir(path.join(this.notificationsDir, 'archive'));

    // Start SSE server if enabled
    if (this.options.enableSSE) {
      await this.startSSEServer();
    }

    // Initialize MCP if enabled
    if (this.options.enableMCP) {
      await this.initializeMCP();
    }

    logger.info('Notification manager initialized');
  }

  private async startSSEServer(): Promise<void> {
    return new Promise(resolve => {
      this.sseServer = http.createServer((req, res) => {
        if (req.url === '/events') {
          // SSE headers
          res.writeHead(200, {
            'Content-Type': 'text/event-stream',
            'Cache-Control': 'no-cache',
            Connection: 'keep-alive',
            'Access-Control-Allow-Origin': '*',
          });

          // Send initial connection message
          res.write('event: connected\n');
          res.write(`data: ${JSON.stringify({ message: 'Connected to notification stream' })}\n\n`);

          // Add client to set
          this.sseClients.add(res);

          // Remove client on disconnect
          req.on('close', () => {
            this.sseClients.delete(res);
          });

          // Keep alive
          const keepAlive = setInterval(() => {
            res.write(':ping\n\n');
          }, 30000);

          req.on('close', () => {
            clearInterval(keepAlive);
          });
        } else {
          res.writeHead(404);
          res.end();
        }
      });

      this.sseServer.listen(this.options.ssePort, () => {
        logger.info(`SSE server started on port ${this.options.ssePort}`);
        resolve();
      });
    });
  }

  private async initializeMCP(): Promise<void> {
    // MCP notification extension would be implemented here
    // For now, we'll use MCP tools to send notifications
    logger.info('MCP notification channel initialized');
  }

  async stop(): Promise<void> {
    if (this.sseServer) {
      this.sseServer.close();
      this.sseClients.clear();
    }

    if (this.mcpProcess) {
      this.mcpProcess.kill();
    }
  }

  async send(notification: Omit<Notification, 'id' | 'timestamp'>): Promise<Notification> {
    const fullNotification: Notification = {
      ...notification,
      id: uuidv4(),
      timestamp: new Date().toISOString(),
    };

    // Validate notification
    const validated = notificationSchema.parse(fullNotification);

    // Send through all enabled channels
    const promises: Promise<void>[] = [];

    for (const [channelId, channel] of this.channels) {
      if (channel.enabled) {
        promises.push(this.sendToChannel(validated, channel));
      }
    }

    await Promise.allSettled(promises);

    // Emit local event
    this.emit('notification', validated);

    return validated;
  }

  private async sendToChannel(
    notification: Notification,
    channel: NotificationChannel
  ): Promise<void> {
    try {
      switch (channel.type) {
        case 'file':
          await this.saveToFile(notification);
          break;

        case 'sse':
          await this.sendSSE(notification);
          break;

        case 'mcp':
          await this.sendMCP(notification);
          break;

        case 'webhook':
          await this.sendWebhook(notification, channel.config);
          break;

        default:
          logger.warn(`Unknown channel type: ${channel.type}`);
      }
    } catch (error) {
      logger.error(`Failed to send notification to channel ${channel.id}:`, error);
    }
  }

  private async saveToFile(notification: Notification): Promise<void> {
    const filename = `${notification.timestamp.replace(/[:.]/g, '-')}-${notification.id}.json`;
    const filepath = path.join(this.notificationsDir, 'inbox', filename);
    await fs.writeJson(filepath, notification, { spaces: 2 });
  }

  private async sendSSE(notification: Notification): Promise<void> {
    const eventData = JSON.stringify(notification);
    const event = `event: ${notification.type}\ndata: ${eventData}\n\n`;

    // Send to all connected clients
    for (const client of this.sseClients) {
      try {
        client.write(event);
      } catch (error) {
        // Client disconnected, remove from set
        this.sseClients.delete(client);
      }
    }
  }

  private async sendMCP(notification: Notification): Promise<void> {
    // In a real implementation, this would use MCP protocol
    // For now, we'll log it as a placeholder
    logger.debug('MCP notification:', notification);

    // Future: Use MCP tool to send notification
    // await this.mcpClient.callTool('notify', { notification });
  }

  private async sendWebhook(notification: Notification, config: any): Promise<void> {
    if (!config?.url) {
      throw new Error('Webhook URL not configured');
    }

    const response = await fetch(config.url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(config.headers || {}),
      },
      body: JSON.stringify(notification),
    });

    if (!response.ok) {
      throw new Error(`Webhook failed: ${response.statusText}`);
    }
  }

  // Convenience methods for common notifications
  async notifyTaskAssigned(taskId: string, assigneeId: string, assignedBy: string): Promise<void> {
    const assignee = this.teamManager.getMemberById(assigneeId);
    const assigner = this.teamManager.getMemberById(assignedBy);

    await this.send({
      type: 'task',
      title: 'Task Assigned',
      message: `${assigner?.name || 'Someone'} assigned you a task`,
      from: assignedBy,
      to: assigneeId,
      priority: 'medium',
      data: { taskId, assigneeId, assignedBy },
      actions: [
        { label: 'View Task', action: 'view-task', primary: true },
        { label: 'Start Task', action: 'start-task' },
      ],
    });
  }

  async notifyTaskCompleted(taskId: string, completedBy: string): Promise<void> {
    const member = this.teamManager.getMemberById(completedBy);

    await this.send({
      type: 'success',
      title: 'Task Completed',
      message: `${member?.name || 'A team member'} completed a task`,
      from: completedBy,
      priority: 'low',
      data: { taskId, completedBy },
    });
  }

  async notifyTeamUpdate(message: string, from: string): Promise<void> {
    await this.send({
      type: 'team',
      title: 'Team Update',
      message,
      from,
      priority: 'medium',
    });
  }

  // Query methods
  async getUnreadNotifications(userId?: string): Promise<Notification[]> {
    const inboxDir = path.join(this.notificationsDir, 'inbox');
    const files = await fs.readdir(inboxDir);

    const notifications: Notification[] = [];

    for (const file of files) {
      if (file.endsWith('.json')) {
        try {
          const notification = await fs.readJson(path.join(inboxDir, file));

          // Filter by recipient if specified
          if (!userId || !notification.to || notification.to === userId) {
            if (!notification.read) {
              notifications.push(notification);
            }
          }
        } catch (error) {
          logger.error(`Failed to read notification ${file}:`, error);
        }
      }
    }

    // Sort by timestamp descending
    return notifications.sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }

  async markAsRead(notificationId: string): Promise<void> {
    const inboxDir = path.join(this.notificationsDir, 'inbox');
    const files = await fs.readdir(inboxDir);

    for (const file of files) {
      if (file.includes(notificationId)) {
        const filepath = path.join(inboxDir, file);
        const notification = await fs.readJson(filepath);
        notification.read = true;
        await fs.writeJson(filepath, notification, { spaces: 2 });
        break;
      }
    }
  }

  async archiveOldNotifications(daysOld = 7): Promise<number> {
    const inboxDir = path.join(this.notificationsDir, 'inbox');
    const archiveDir = path.join(this.notificationsDir, 'archive');
    const files = await fs.readdir(inboxDir);

    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysOld);

    let archived = 0;

    for (const file of files) {
      if (file.endsWith('.json')) {
        try {
          const filepath = path.join(inboxDir, file);
          const notification = await fs.readJson(filepath);

          if (new Date(notification.timestamp) < cutoffDate && notification.read) {
            await fs.move(filepath, path.join(archiveDir, file));
            archived++;
          }
        } catch (error) {
          logger.error(`Failed to archive ${file}:`, error);
        }
      }
    }

    return archived;
  }

  // SSE client helper for web apps
  getSSEEndpoint(): string {
    return `http://localhost:${this.options.ssePort}/events`;
  }
}
